"use client";

import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { DataTable, createSelectColumn } from "@/src/components/information/data-table";
import { Text, Progress } from "@/src/components";
import { ComponentPage } from "@/components/docs/component-page";
import { cn } from "@/utils/utils";

type ProjectRow = {
  name: string;
  client: string;
  avatar: string;
  start: string;
  deadline: string;
  status: "Active" | "Cancel" | "Completed" | "Pending";
  progress: number;
};

const statusClass = (s: ProjectRow["status"]) =>
  cn(
    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
    s === "Active" && "bg-success-bg text-success",
    s === "Cancel" && "bg-error-bg text-destructive",
    s === "Completed" && "bg-info-bg text-info",
    s === "Pending" && "bg-warning-bg text-warning"
  );

export default function DocsComponentsDataTable() {
  const demoData: ProjectRow[] = [
    {
      name: "Website Redesign",
      client: "Bluecopa",
      avatar: "/vercel.svg",
      start: "2025-09-01",
      deadline: "2025-11-15",
      status: "Active",
      progress: 64,
    },
    {
      name: "Mobile App",
      client: "Globex",
      avatar: "/vercel.svg",
      start: "2025-08-12",
      deadline: "2025-12-01",
      status: "Pending",
      progress: 28,
    },
    {
      name: "Marketing Site",
      client: "Initech",
      avatar: "/vercel.svg",
      start: "2025-07-10",
      deadline: "2025-10-20",
      status: "Completed",
      progress: 100,
    },
  ];

  const columns = React.useMemo<ColumnDef<ProjectRow, any>[]>(() => {
    return [
      createSelectColumn<ProjectRow>(),
      {
        accessorKey: "name",
        header: () => "Project Name",
        cell: ({ row }) => (
          <Text as="div" className="font-medium">
            {row.original.name}
          </Text>
        ),
      },
      {
        accessorKey: "client",
        header: () => "Client Name",
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <img
              src={row.original.avatar}
              alt={row.original.client}
              className="h-8 w-8 rounded-full object-cover"
            />
            <Text as="div">{row.original.client}</Text>
          </div>
        ),
      },
      {
        accessorKey: "start",
        header: () => "Start Date",
        cell: ({ row }) => row.original.start,
      },
      {
        accessorKey: "deadline",
        header: () => "Deadline",
        cell: ({ row }) => row.original.deadline,
      },
      {
        accessorKey: "status",
        header: () => "Status",
        cell: ({ row }) => (
          <div className={statusClass(row.original.status)}>
            {row.original.status}
          </div>
        ),
      },
      {
        accessorKey: "progress",
        header: () => "Progress",
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <Progress
              value={row.original.progress}
              className="h-2 w-24"
              indicatorClassName="bg-foreground"
            />
            <Text as="p" className="text-muted-foreground">
              %{row.original.progress}
            </Text>
          </div>
        ),
      },
    ];
  }, []);

  const code = `import { ColumnDef } from "@tanstack/react-table";
import { DataTable, createSelectColumn } from "@/registry/new-york/blocks/data-table/data-table";
import { Text } from "@/registry/new-york/ui/text";
import { Progress } from "@/registry/new-york/ui/progress";

type ProjectRow = {
  name: string;
  client: string;
  avatar: string;
  start: string;
  deadline: string;
  status: "Active" | "Cancel" | "Completed" | "Pending";
  progress: number;
};

export function DataTableDemo() {
  const data: ProjectRow[] = [
    {
      name: "Website Redesign",
      client: "Bluecopa",
      avatar: "/vercel.svg",
      start: "2025-09-01",
      deadline: "2025-11-15",
      status: "Active",
      progress: 64,
    },
    // ... more data
  ];

  const columns: ColumnDef<ProjectRow, any>[] = [
    createSelectColumn<ProjectRow>(),
    {
      accessorKey: "name",
      header: () => "Project Name",
      cell: ({ row }) => (
        <Text as="div" className="font-medium">
          {row.original.name}
        </Text>
      ),
    },
    {
      accessorKey: "client",
      header: () => "Client Name",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <img
            src={row.original.avatar}
            alt={row.original.client}
            className="h-8 w-8 rounded-full object-cover"
          />
          <Text as="div">{row.original.client}</Text>
        </div>
      ),
    },
    {
      accessorKey: "start",
      header: () => "Start Date",
      cell: ({ row }) => row.original.start,
    },
    {
      accessorKey: "deadline",
      header: () => "Deadline",
      cell: ({ row }) => row.original.deadline,
    },
    {
      accessorKey: "status",
      header: () => "Status",
      cell: ({ row }) => (
        <div className={statusClass(row.original.status)}>
          {row.original.status}
        </div>
      ),
    },
    {
      accessorKey: "progress",
      header: () => "Progress",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Progress
            value={row.original.progress}
            className="h-2 w-24"
            indicatorClassName="bg-foreground"
          />
          <Text as="p" className="text-muted-foreground">
            %{row.original.progress}
          </Text>
        </div>
      ),
    },
  ];

  return (
    <DataTable<ProjectRow, any>
      columns={columns}
      data={data}
      filterColumn="name"
      filterPlaceholder="Filter projects..."
      showToolbar
      showFooter
    />
  );
}`;

  const installCommand = `npx shadcn@latest add @bluecopa-ui/data-table`;

  return (
    <ComponentPage
      title="Data Table"
      description="Advanced data table with sorting, filtering, pagination, and column visibility controls. Powered by TanStack Table."
      preview={
        <div className="w-full h-[400px]">
          <DataTable<ProjectRow, any>
            columns={columns}
            data={demoData}
            filterColumn="name"
            filterPlaceholder="Filter projects..."
            showToolbar
            showFooter
            className="w-full"
          />
        </div>
      }
      code={code}
      installCommand={installCommand}
      apiReference={{
        name: "DataTableProps",
        props: [
          {
            name: "columns",
            type: "ColumnDef<TData, TValue>[]",
            description: "Array of column definitions from TanStack Table.",
          },
          {
            name: "data",
            type: "TData[]",
            description: "Array of data rows to display in the table.",
          },
          {
            name: "filterColumn",
            type: "string",
            description: "The accessorKey of the column to filter against. If provided, shows a filter input in the toolbar.",
          },
          {
            name: "filterPlaceholder",
            type: "string",
            description: "Placeholder text for the filter input. Defaults to 'Filter...'.",
          },
          {
            name: "showToolbar",
            type: "boolean",
            description: "Whether to show the toolbar with filter and column visibility controls. Defaults to true.",
          },
          {
            name: "showFooter",
            type: "boolean",
            description: "Whether to show the footer with row count and pagination. Defaults to true.",
          },
          {
            name: "emptyState",
            type: "React.ReactNode",
            description: "Custom content to display when the table has no data.",
          },
          {
            name: "showBorder",
            type: "boolean",
            description: "Whether to show a border around the table. Defaults to true.",
          },
          {
            name: "className",
            type: "string",
            description: "Additional CSS classes to apply to the table container.",
          },
        ],
      }}
    />
  );
}

